import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from '@gto/ui/button'

import type { AffiliatePartnerAssetSchema } from '@/api/gen'
import { useListPartnerAssets } from '@/api/gen'
import { AssetCard } from '@/components/asset-card'
import { AssetGrid } from '@/components/asset-grid'
import { AssetGridSkeleton } from '@/components/asset-grid-skeleton'
import { AssetPreviewDialog } from '@/components/asset-preview-dialog'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { TableFilterBar } from '@/components/table-filter-bar'
import { useReportQueryError } from '@/hooks/use-report-query-error'

const CATEGORY_LABEL_IDS: Record<string, string> = {
  ALL: 'assets.category.all',
  BANNER: 'assets.category.banners',
  IMAGE: 'assets.category.images',
  VIDEO: 'assets.category.videos',
  OTHER: 'assets.category.other',
}

const CATEGORY_VALUES = ['ALL', 'BANNER', 'IMAGE', 'VIDEO', 'OTHER'] as const

export const AssetsPage = () => {
  const { formatMessage } = useIntl()
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [previewAsset, setPreviewAsset] = useState<AffiliatePartnerAssetSchema | null>(null)

  // ONE unfiltered query; the category filter is client-side — same recipe as
  // the admin page: true counts on the pills, instant switching, no refetch.
  const assetsQuery = useListPartnerAssets()
  const { data: assets, isLoading, isError, refetch } = assetsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  useReportQueryError(assetsQuery, 'useListPartnerAssets failed: ')

  const rows = assets ?? []
  const visibleRows =
    categoryFilter === 'ALL' ? rows : rows.filter((asset) => asset.category === categoryFilter)

  const categoryOptions = CATEGORY_VALUES.map((value) => ({
    value,
    label: formatMessage({ id: CATEGORY_LABEL_IDS[value] }),
    count: isLoading
      ? undefined
      : value === 'ALL'
        ? rows.length
        : rows.filter((asset) => asset.category === value).length,
  }))

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="assets.title" />} />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="assets-error"
            messageId="assets.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <TableFilterBar
              options={categoryOptions}
              value={categoryFilter}
              onValueChange={setCategoryFilter}
            />

            {isLoading ? (
              <AssetGridSkeleton />
            ) : rows.length === 0 ? (
              <EmptyState
                description={<FormattedMessage id="assets.empty.description" />}
                title={<FormattedMessage id="assets.empty" />}
              />
            ) : visibleRows.length === 0 ? (
              <EmptyState
                title={<FormattedMessage id="assets.empty.category" />}
                action={
                  <Button
                    variant="outlined"
                    onClick={() => setCategoryFilter('ALL')}
                  >
                    <FormattedMessage id="assets.empty.showAll" />
                  </Button>
                }
              />
            ) : (
              <AssetGrid>
                {visibleRows.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    category={asset.category}
                    contentType={asset.content_type}
                    downloadUrl={asset.download_url ?? null}
                    fileSize={asset.file_size}
                    name={asset.name}
                    onPreview={() => setPreviewAsset(asset)}
                  />
                ))}
              </AssetGrid>
            )}
          </>
        )}
      </Page.Content>

      {previewAsset && (
        <AssetPreviewDialog
          open
          category={previewAsset.category}
          contentType={previewAsset.content_type}
          downloadUrl={previewAsset.download_url ?? null}
          fileSize={previewAsset.file_size}
          name={previewAsset.name}
          onClose={() => setPreviewAsset(null)}
        />
      )}
    </Page>
  )
}
